import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { LenderFieldMap } from '@trnsact/trnsact-shared-types';
import _ from 'lodash';
import { GET_DEPENDENT_OPTION_SETS } from '../api/getDependentOptionSets';
import { GetDependentOptionSetsResponse, GetDependentOptionSetsVariables } from '../api/types';

interface Options {
  getEngine: () => any;
  dynamicLenderMappings: LenderFieldMap[];
  onMappingsChange: (updatedMappings: LenderFieldMap[]) => void;
  setFormValue: (fieldId: string, value: any) => void;
}

function cloneMappings(mappings: LenderFieldMap[]): LenderFieldMap[] {
  return mappings.map(m => ({
    ...m,
    details: m.details ? { ...m.details } : m.details,
  }));
}

export function useDlmRulesEventsHandler({
  getEngine,
  dynamicLenderMappings,
  onMappingsChange,
  setFormValue,
}: Options) {
  const apolloClient = useApolloClient();

  const handleDlmRulesEvents = useCallback(
    (events: any[]) => {
      const engine = getEngine();
      const updatedMappings = cloneMappings(dynamicLenderMappings);
      let hasChanges = false;

      const setMappingValue = (inputKey: string, value: any) => {
        const dlmInputIndex = updatedMappings.findIndex(el => el.lenderProperty === inputKey);
        if (dlmInputIndex !== -1) {
          updatedMappings[dlmInputIndex].mappedValue = value;
          setFormValue(updatedMappings[dlmInputIndex].lenderFieldId!, value);
          hasChanges = true;
        }
      };

      events.forEach((event: any) => {
        if (!event.params?.actions) return;

        event.params.actions.forEach((action: any) => {
          if (action.type === 'setInputValue') {
            if (_.has(action, 'value') && action.inputKey) {
              setMappingValue(action.inputKey, action.value);
            } else if (action.factKey && action.inputKey) {
              const fact = engine.getFact(action.factKey);

              if (!fact) {
                return;
              }

              setMappingValue(action.inputKey, (fact as any).value);
            } else if (action.operations && _.isArray(action.operations) && action.inputKey) {
              try {
                let finalValue: number | undefined;
                action.operations.forEach((o: any) => {
                  const operation = o.operation;
                  const outputType = o.outputType;
                  if (operation && o.operands && _.isArray(o.operands)) {
                    o.operands.forEach((operand: any) => {
                      let operandValue: number | undefined;
                      if (_.isObject(operand) && (operand as any).factKey) {
                        const fact = engine.getFact((operand as any).factKey);
                        if (_.isString((fact as any).value)) {
                          operandValue =
                            outputType === 'INTEGER' ? parseInt((fact as any).value) : parseFloat((fact as any).value);
                        } else {
                          operandValue = (fact as any).value;
                        }
                      } else {
                        operandValue = operand;
                      }

                      if (finalValue === undefined && _.isNumber(operandValue)) {
                        finalValue = operandValue;
                      } else if (operandValue !== undefined) {
                        if (operation === 'DIFFERENCE') {
                          finalValue = (finalValue ?? 0) - operandValue;
                        } else if (operation === 'SUM') {
                          finalValue = (finalValue ?? 0) + operandValue;
                        }
                      }
                    });
                  } else {
                    console.warn(
                      `setInputValue: triggered with operations but no value could be found: ${JSON.stringify(action)}`
                    );
                  }
                });
                setMappingValue(action.inputKey, finalValue);
              } catch (e: any) {
                console.error(
                  `setInputValue triggered with operations but an error occurred: ${JSON.stringify(action)}: ${e.message}`
                );
              }
            } else {
              console.warn(
                `setInputValue: ${action.inputKey}: triggered but no value was found: ${JSON.stringify(action)}`
              );
            }
          } else if (action.type === 'setInputRequired') {
            try {
              const dlmInputIndex = updatedMappings.findIndex(el => el.lenderProperty === action.inputKey);
              if (dlmInputIndex !== -1) {
                (updatedMappings[dlmInputIndex].details as any).required = action.value ? 'REQUIRED' : 'OPTIONAL';
                hasChanges = true;
              }
            } catch (e: any) {
              console.log(`Error setting setInputRequired[${action.inputKey}]: ${e.message}`);
            }
          } else if (action.type === 'setInputHidden') {
            try {
              let dlmInputIndex = updatedMappings.findIndex(el => el.lenderProperty === action.inputKey);
              if (dlmInputIndex !== -1) {
                updatedMappings[dlmInputIndex].details!.hidden = action.value;
                engine.addFact(`${action.inputKey}_hidden`, action.value);
                hasChanges = true;
              } else {
                let inputIteratorToHide = 0;
                do {
                  dlmInputIndex = updatedMappings.findIndex(
                    el => el.lenderProperty === `${action.inputKey}[${inputIteratorToHide}]`
                  );
                  if (dlmInputIndex !== -1) {
                    updatedMappings[dlmInputIndex].details!.hidden = action.value;
                    engine.addFact(`${action.inputKey}_hidden`, action.value);
                    hasChanges = true;
                  }
                  inputIteratorToHide++;
                } while (dlmInputIndex !== -1);
              }
            } catch (e: any) {
              console.log(`Error setting setInputHidden[${action.inputKey}]: ${e.message}`);
            }
          } else if (action.type === 'setCategoryVisible') {
            try {
              updatedMappings.forEach((dlmField, index) => {
                if (dlmField.details?.category === action.categoryName) {
                  const visible = action.value === true ? false : true;
                  updatedMappings[index].details!.hidden = visible;
                  engine.addFact(`${dlmField.lenderProperty}_hidden`, visible);
                  hasChanges = true;
                }
              });
            } catch (e: any) {
              console.log(`Error setting setCategoryVisible[${action.categoryName}]: ${e.message}`);
            }
          } else if (action.type === 'setInputError') {
            try {
              const dlmInputIndex = updatedMappings.findIndex(el => el.lenderProperty === action.inputKey);
              let currentErrors: any[] = _.get(updatedMappings[dlmInputIndex], 'details.errors', []);
              if (!currentErrors.find((error: any) => error.name === action.error.name)) {
                currentErrors.push(action.error);
                (updatedMappings[dlmInputIndex].details as any).errors = currentErrors;
                hasChanges = true;
              }
            } catch (e: any) {
              console.log(`Error setting setInputError[${action.inputKey}]: ${e.message}`);
            }
          } else if (action.type === 'removeInputError') {
            try {
              const dlmInputIndex = updatedMappings.findIndex(el => el.lenderProperty === action.inputKey);
              let currentErrors = _.get(updatedMappings[dlmInputIndex], 'details.errors', []);
              (updatedMappings[dlmInputIndex].details as any).errors = currentErrors.filter(
                (error: any) => !action.errorsToClear.includes(error.name)
              );
              hasChanges = true;
            } catch (e: any) {
              console.log(`Error setting removeInputError[${action.inputKey}]: ${e.message}`);
            }
          } else if (action.type === 'removeRule') {
            engine.removeRule(action.ruleName);
          } else if (action.type === 'setOptionSetInputValue') {
            try {
              const optionSetsFact = engine.getFact(`${action.inputKey}-optionSets`);
              const likeDisplay = action.likeDisplay;
              const optionSet = (optionSetsFact as any).value.find((os: any) => {
                return os.displayName.toLowerCase().includes(likeDisplay.toLowerCase());
              });
              if (optionSet) {
                setMappingValue(action.inputKey, optionSet.lenderValue);
              }
            } catch (e: any) {
              console.log(`Error setting default option set for ${action.inputKey}: ${e.message}`);
            }
          } else if (action.type === 'getCascadingOptionSets') {
            const parentSelectedValue = engine.getFact(action.parentInputKey);
            const parentOptionSets = engine.getFact(`${action.parentInputKey}-optionSets`);

            if (parentOptionSets) {
              const selectedParentOptionSet = (parentOptionSets as any).value?.find((os: any) => {
                return os.lenderValue === (parentSelectedValue as any).value;
              });

              if (selectedParentOptionSet?.lenderFieldOptionSetId) {
                apolloClient
                  .query<GetDependentOptionSetsResponse, GetDependentOptionSetsVariables>({
                    query: GET_DEPENDENT_OPTION_SETS,
                    variables: {
                      lenderFieldId: action.lenderFieldId,
                      lenderFieldParentSelectedOptionSetId: selectedParentOptionSet.lenderFieldOptionSetId,
                    },
                    fetchPolicy: 'no-cache',
                  })
                  .then(result => {
                    const dependentOptionSets = result.data.getDependentOptionSets;

                    const dlmInputIndex = updatedMappings.findIndex(m => m.lenderFieldId === action.lenderFieldId);
                    if (dlmInputIndex !== -1) {
                      (updatedMappings[dlmInputIndex].details as any).optionSets = dependentOptionSets;
                      onMappingsChange([...updatedMappings]);
                    }

                    engine.addFact(`${action.inputKey}-optionSets`, dependentOptionSets);
                  });
              }
            }
          }
        });
      });

      if (hasChanges) {
        onMappingsChange(updatedMappings);
      }
    },
    [getEngine, dynamicLenderMappings, apolloClient, onMappingsChange, setFormValue]
  );

  return {
    handleDlmRulesEvents,
  };
}
